import { Controller } from "@hotwired/stimulus"

const MAX_TABS = 12
const SYNC_DELAY = 1000
const FALLBACK_URL = "/orders"

// Persistent "open record" tab strip.
//
// The root element is `data-turbo-permanent`, so connect() runs ONCE per full
// page load — every per-visit concern hangs off `turbo:load` instead. The
// browser is the source of truth: state lives in localStorage under
// `ws:tabs:<company id>` (namespaced per tenant, so switching companies never
// surfaces another company's tabs). The server copy is a debounced,
// fire-and-forget mirror so the tabs follow the user to another device.
export default class extends Controller {
  static values = { scope: String, syncUrl: String, initial: Array }

  connect() {
    this.tabs = []
    this.recent = []   // urls, least-recently-activated first
    this.active = null
    this.syncTimeout = null
    this.onTurboLoad = this.onTurboLoad.bind(this)
    this.onPageHide = this.onPageHide.bind(this)

    this.load()
    document.addEventListener("turbo:load", this.onTurboLoad)
    window.addEventListener("pagehide", this.onPageHide)
    this.onTurboLoad()
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.onTurboLoad)
    window.removeEventListener("pagehide", this.onPageHide)
    this.flushSync()
  }

  // The strip is data-turbo-permanent, so it only disconnects when the page
  // really goes away — send what the debounce still owes instead of dropping
  // it. `keepalive` lets the request outlive the unloading document.
  onPageHide() {
    this.flushSync({ keepalive: true })
  }

  // --- per-visit ----------------------------------------------------------

  onTurboLoad() {
    const tab = this.declaredTab()
    if (tab) {
      this.open(tab)
      return
    }

    // Index pages, dashboard, settings: no record is open.
    if (this.active !== null) {
      this.active = null
      this.save()
    }
    this.render()
  }

  declaredTab() {
    const meta = document.querySelector('meta[name="ws-tab"]')
    if (!meta) return null

    try {
      const tab = JSON.parse(meta.content)
      return tab && tab.url ? tab : null
    } catch (error) {
      return null
    }
  }

  open(tab) {
    const existing = this.tabs.find((t) => t.url === tab.url)
    if (existing) {
      existing.label = tab.label
      existing.sub = tab.sub
    } else {
      this.tabs.push({ url: tab.url, type: tab.type, label: tab.label, sub: tab.sub })
    }

    this.active = tab.url
    this.touch(tab.url)
    this.evict()
    this.save()
    this.render()
  }

  // --- actions ------------------------------------------------------------

  activate(event) {
    // Leave modified / non-primary clicks to the browser (open in new tab,
    // new window, download) — only a plain left click becomes a Turbo visit.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (event.button !== 0) return

    const url = event.currentTarget.dataset.wsUrl
    if (!url) return

    event.preventDefault()
    this.visit(url)
  }

  // Handles both the × button (click) and a middle-click on the chip itself.
  close(event) {
    if (event.type === "auxclick" && event.button !== 1) return

    const chip = event.currentTarget.closest(".ws-tab")
    if (!chip) return

    event.preventDefault()
    event.stopPropagation()

    const url = chip.dataset.wsUrl
    const index = this.tabs.findIndex((t) => t.url === url)
    const wasActive = url === this.active
    this.remove(url)

    let neighbor = null
    if (wasActive) {
      neighbor = this.tabs[index] || this.tabs[index - 1] || null
      this.active = neighbor ? neighbor.url : null
    }

    this.save()
    this.render()
    if (wasActive) this.visit(neighbor ? neighbor.url : FALLBACK_URL)
  }

  visit(url) {
    if (window.Turbo && typeof window.Turbo.visit === "function") {
      window.Turbo.visit(url)
    } else {
      window.location.href = url
    }
  }

  // --- state --------------------------------------------------------------

  get storageKey() {
    return `ws:tabs:${this.scopeValue}`
  }

  load() {
    let state = null
    try {
      const raw = window.localStorage.getItem(this.storageKey)
      if (raw) state = JSON.parse(raw)
    } catch (error) {
      state = null
    }

    if (state && Array.isArray(state.tabs)) {
      this.tabs = state.tabs
      this.recent = Array.isArray(state.recent) ? state.recent : []
      this.active = state.active || null
    } else {
      this.tabs = this.hasInitialValue ? this.initialValue.slice() : []
    }
  }

  save() {
    const state = { tabs: this.tabs, active: this.active, recent: this.recent }
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(state))
    } catch (error) {
      // Storage unavailable (private mode / quota) — the server mirror still runs.
    }
    this.scheduleSync()
  }

  touch(url) {
    this.recent = this.recent.filter((u) => u !== url)
    this.recent.push(url)
  }

  remove(url) {
    this.tabs = this.tabs.filter((t) => t.url !== url)
    this.recent = this.recent.filter((u) => u !== url)
  }

  // Cap the strip: the least-recently-activated tab loses. Never evicts the
  // tab that was just opened. Tabs restored from the server have no recorded
  // activation (index -1) and are therefore evicted first.
  evict() {
    while (this.tabs.length > MAX_TABS) {
      const candidates = this.tabs.filter((t) => t.url !== this.active)
      if (candidates.length === 0) return

      const victim = candidates.reduce((oldest, tab) =>
        this.recent.indexOf(tab.url) < this.recent.indexOf(oldest.url) ? tab : oldest)
      this.remove(victim.url)
    }
  }

  scheduleSync() {
    if (!this.hasSyncUrlValue) return

    clearTimeout(this.syncTimeout)
    this.syncTimeout = setTimeout(() => {
      this.syncTimeout = null
      this.sync()
    }, SYNC_DELAY)
  }

  flushSync(options = {}) {
    if (!this.syncTimeout) return

    clearTimeout(this.syncTimeout)
    this.syncTimeout = null
    this.sync(options)
  }

  sync({ keepalive = false } = {}) {
    const csrf = document.querySelector('meta[name="csrf-token"]')
    // Belt-and-braces: the server rejects the WHOLE payload over these limits.
    // workspace_tab_meta already truncates at the source; a tab restored from
    // older localStorage could still be long.
    const tabs = this.tabs.map(({ url, type, label, sub }) => ({
      url,
      type,
      label: String(label == null ? "" : label).slice(0, 60),
      sub: sub ? String(sub).slice(0, 80) : sub
    }))

    fetch(this.syncUrlValue, {
      method: "PATCH",
      credentials: "same-origin",
      keepalive,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrf ? csrf.content : ""
      },
      body: JSON.stringify({ tabs })
    }).catch((error) => console.warn("workspace-tabs: sync failed", error))
  }

  // --- rendering ----------------------------------------------------------

  render() {
    const nav = this.nav()
    nav.replaceChildren()
    this.tabs.forEach((tab) => nav.appendChild(this.chip(tab)))
    this.element.hidden = this.tabs.length === 0
  }

  nav() {
    let nav = this.element.querySelector(".ws-tabs")
    if (!nav) {
      nav = document.createElement("nav")
      nav.className = "ws-tabs"
      nav.setAttribute("aria-label", "Open records")
      this.element.appendChild(nav)
    }
    return nav
  }

  chip(tab) {
    const chip = document.createElement("a")
    chip.className = tab.url === this.active ? "ws-tab ws-tab--active" : "ws-tab"
    chip.href = tab.url
    chip.dataset.wsUrl = tab.url
    chip.dataset.action = "click->workspace-tabs#activate auxclick->workspace-tabs#close"

    // textContent, never innerHTML: labels are user-supplied record data.
    const label = document.createElement("span")
    label.className = "ws-tab__label"
    label.textContent = tab.label || tab.url
    chip.appendChild(label)

    if (tab.sub) {
      const sub = document.createElement("span")
      sub.className = "ws-tab__sub"
      sub.textContent = tab.sub
      chip.appendChild(sub)
    }

    const close = document.createElement("button")
    close.type = "button"
    close.className = "ws-tab__close"
    close.setAttribute("aria-label", `Close ${tab.label || "tab"}`)
    close.dataset.action = "click->workspace-tabs#close"
    close.textContent = "×"
    chip.appendChild(close)

    return chip
  }
}
